import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { fetchSurvey, type SurveyMeta } from '../api'
import { loadToken, type DecodedToken } from '../_shared/token'
import {
  Badge,
  Card,
  ErrorPanel,
  Footer,
  FullPageLoader,
  Header,
  Page,
} from '../_shared/ui'
import { formatMoney } from '../_shared/format'

interface MethodCardProps {
  title: string
  subtitle: string
  badge?: string
  disabled?: boolean
  onSelect: () => void
}

function MethodCard({ title, subtitle, badge, disabled, onSelect }: MethodCardProps) {
  return (
    <button
      onClick={onSelect}
      disabled={disabled}
      style={{
        textAlign: 'left',
        width: '100%',
        background: 'var(--cp-bg-elevated)',
        border: '1px solid var(--cp-rule)',
        borderRadius: 'var(--cp-radius-lg)',
        padding: 'var(--cp-sp-4)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all var(--cp-dur-fast)',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
      onMouseOver={(e) => {
        if (!disabled) {
          ;(e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--cp-accent-primary)'
          ;(e.currentTarget as HTMLButtonElement).style.boxShadow = 'var(--cp-shadow-md)'
        }
      }}
      onMouseOut={(e) => {
        ;(e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--cp-rule)'
        ;(e.currentTarget as HTMLButtonElement).style.boxShadow = 'none'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 600, fontSize: 'var(--cp-text-md)' }}>{title}</span>
        {badge && <Badge variant="info">{badge}</Badge>}
      </div>
      <span style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-fg-muted)' }}>{subtitle}</span>
    </button>
  )
}

export default function MethodPicker() {
  const { surveyId = '' } = useParams()
  const navigate = useNavigate()
  const [search, setSearch] = useSearchParams()
  const [survey, setSurvey] = useState<SurveyMeta | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [decoded, setDecoded] = useState<DecodedToken | null>(null)
  const [tokenMissing, setTokenMissing] = useState(false)

  useEffect(() => {
    if (!surveyId || surveyId === 'missing') {
      setLoading(false)
      setError('No survey id was provided in the link. Please check the link your sponsor sent you.')
      return
    }
    const { token, decoded: d } = loadToken(surveyId, search.toString())
    if (!token) {
      setTokenMissing(true)
    } else if (d) {
      setDecoded(d)
    }

    // Clean ?token=... out of the URL bar once we've persisted it
    if (search.has('token')) {
      search.delete('token')
      setSearch(search, { replace: true })
    }

    let cancelled = false
    fetchSurvey(surveyId).then((res) => {
      if (cancelled) return
      if (!res.ok) {
        setError(humanError(res.error))
      } else {
        setSurvey(res.data)
      }
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [surveyId, search, setSearch])

  if (loading) return <FullPageLoader />

  if (error) {
    return (
      <Page>
        <Header />
        <ErrorPanel title="Cannot load survey" body={error} />
        <Footer surveyId={surveyId} />
      </Page>
    )
  }
  if (!survey) return <FullPageLoader />

  if (tokenMissing) {
    return (
      <Page>
        <Header surveyName={survey.name} sponsorName={survey.sponsor.name} />
        <ErrorPanel
          title="Missing payment token"
          body={
            <>
              The link you used does not include a valid payment token. Please return to the
              survey and use the personal payment link your sponsor sent at completion. If you
              already completed the survey, please contact {survey.sponsor.name}.
            </>
          }
        />
        <Footer surveyId={surveyId} />
      </Page>
    )
  }

  const matchingTier = survey.tiers.find((t) => t.name === decoded?.tier)
  if (!matchingTier) {
    return (
      <Page>
        <Header surveyName={survey.name} sponsorName={survey.sponsor.name} />
        <ErrorPanel
          title="Payment tier no longer exists"
          body={
            <>
              The tier on your payment link ({decoded?.tier ?? 'unknown'}) is no longer part of
              this survey. Please contact {survey.sponsor.name}.
            </>
          }
        />
        <Footer surveyId={surveyId} />
      </Page>
    )
  }

  if (decoded?.expired) {
    return (
      <Page>
        <Header surveyName={survey.name} sponsorName={survey.sponsor.name} />
        <ErrorPanel
          title="Payment link expired"
          body={
            <>
              This payment link is no longer valid. Please contact {survey.sponsor.name} to be
              re-issued a fresh link.
            </>
          }
        />
        <Footer surveyId={surveyId} />
      </Page>
    )
  }

  const sepaEnabled = survey.methods.includes('sepa')
  const usdcEnabled = survey.methods.includes('usdc')
  const lightningEnabled = survey.methods.includes('lightning')

  return (
    <Page>
      <Header
        surveyName={survey.name}
        sponsorName={survey.sponsor.name}
        badge={survey.dryRun ? <Badge variant="warning">Test mode</Badge> : null}
      />

      <Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-fg-muted)' }}>
            Your honorarium
          </div>
          <div
            className="cp-tabular"
            style={{ fontSize: 'var(--cp-text-3xl)', fontWeight: 600 }}
          >
            {formatMoney(matchingTier.amount, matchingTier.currency)}
          </div>
          <div style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-fg-muted)' }}>
            Tier: {matchingTier.name} · Settles {survey.expectedSettlement}
          </div>
        </div>
      </Card>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 'var(--cp-text-sm)', fontWeight: 500 }}>
          Choose how you want to receive payment
        </div>
        {sepaEnabled && (
          <MethodCard
            title="Bank transfer (SEPA Instant)"
            subtitle="EUR to your IBAN. Best for EU panellists. Funds arrive in ~15 seconds."
            onSelect={() => navigate(`/p/${surveyId}/sepa`)}
          />
        )}
        {usdcEnabled && (
          <MethodCard
            title="Stablecoin (USDC on Polygon)"
            subtitle="Receive the equivalent in USDC. Works anywhere a wallet does."
            badge="Crypto"
            onSelect={() => navigate(`/p/${surveyId}/usdc`)}
          />
        )}
        {lightningEnabled && (
          <MethodCard
            title="Bitcoin (Lightning)"
            subtitle="Instant payout in sats to your Lightning address or invoice. EUR rate locked for 60 seconds at confirmation."
            badge="Crypto"
            onSelect={() => navigate(`/p/${surveyId}/lightning`)}
          />
        )}
        {!sepaEnabled && !usdcEnabled && !lightningEnabled && (
          <ErrorPanel
            title="No payment methods enabled"
            body={
              <>
                The sponsor has not enabled any panellist payment methods for this survey. Please
                contact {survey.sponsor.name}.
              </>
            }
          />
        )}
      </div>

      <div style={{ fontSize: 'var(--cp-text-xs)', color: 'var(--cp-fg-muted)' }}>
        {survey.vatNote}
      </div>

      <Footer surveyId={surveyId} />
    </Page>
  )
}

function humanError(err: string): string {
  switch (err) {
    case 'survey not found':
    case 'survey_not_found':
      return 'We could not find this survey. The link may be incorrect.'
    case 'survey not available':
    case 'survey_not_available':
      return 'This survey is not currently accepting payouts. Please contact your sponsor.'
    default:
      return `Unable to load this survey (${err}). Please contact your sponsor.`
  }
}
